import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import queryString from 'query-string';
import { tp } from 'app/utils/typograf';

import {
    CATEGORY_MAIN,
    CATEGORY_READY_TO_EAT,
    CATEGORY_SNACKS,
    CATEGORY_TITLES_MOBILE,
    isCategoryVisibleForLocation,
    SUBCATEGORIES_ANALYTICS_TITLES,
    SUBCATEGORIES_ORDER,
} from 'app/const/categories';
import { useCategoryLocationKey } from 'app/hooks/accessors';
import scrollObserver from 'app/utils/scrollObserver';

import Responsive from 'app/components/Responsive';
import { UIHeading } from 'app/components/ui';

import { isDesktop } from 'app/utils/resolution';
import {
    MenuDataContext,
    MenuDispatchContext,
    TOP,
    CATEGORY,
    FIRST_CATEGORY,
    STORED_POSITION,
} from 'app/containers/contexts/menu.context';

import '../../../../../../components/MenuCategory/menu-category.scss';
import { AdditionalDishesSelection } from 'app/components/BR1258/AdditionalDishesSelection/AdditionalDishesSelection';
import { useMenuSelectionsDishes } from 'app/components/BR1258/hooks/useMenuSelectionsDishes';
import { analyticService } from 'global/services';
import { useProductImpressionObserver } from 'global/hooks/useProductImpressionObserver';
import { gql, useFragment } from '@apollo/client';
import { sortBy } from 'lodash';
import DishesV2 from './DishesV2';
import { groupSubcategoriesByName } from '../utils/groupSubcategoriesByName';

const categoryScrollingTypes = [
    TOP, CATEGORY, FIRST_CATEGORY,
];

class MenuCategoryV2 extends React.Component {
    containerRef = React.createRef();

    mainRef = React.createRef();

    pixelForScrollRef = React.createRef();

    positionIsObserved = false;

    componentDidMount() {
        // const { isIntersectingOn } = this.props;
        // if (!isIntersectingOn) {
        //     this.observeViewportPosition();
        // }

        this.scrollCategoryToTop();
    }

    componentDidUpdate() {
        const {
            menuDataContext: { scrolling },
        } = this.props;

        if (categoryScrollingTypes.includes(scrolling.target)) {
            this.scrollCategoryToTop();
        }
    }

    componentWillUnmount() {
        scrollObserver.stopObservingPosition({
            el: this.loadingContainerRef,
        });
    }

    scrollCategoryToTop() {
        const {
            categoryId,
            index,
            menuDataContext: { scrolling },
            menuDispatchContext,
        } = this.props;

        const isSmooth = Boolean(scrolling.smooth);
        const options = isSmooth ? {
            behavior: 'smooth',
        } : undefined;

        switch (scrolling.target) {
            case TOP:
            case FIRST_CATEGORY: {
                window.scrollTo(0, 0, options);
                break;
            }
            case STORED_POSITION: {
                window.scrollTo(0, scrolling.value, options);
                break;
            }
            case CATEGORY: {
                const isFirstCategory = index === 1;
                const isCategoryIdForScrolling = categoryId === scrolling.value;

                if (isFirstCategory) {
                    window.scrollTo(0, 0, options);
                    break;
                }
                if (isCategoryIdForScrolling) {
                    const el = this.pixelForScrollRef.current;
                    el.scrollIntoView(options);
                    setTimeout(() => {
                        window.scrollBy({
                            top: window.innerWidth >= 1366 && categoryId !== '3' ? 0 : -140,
                            behavior: 'smooth',
                        });
                    }, 100);
                    break;
                }
                break;
            }
            default: break;
        }

        menuDispatchContext({ type: 'resetScroll' });
    }

    // observeViewportPosition() {
    //     const { viewportPadding } = this.props;
    //     if (this.containerRef.current && !this.positionIsObserved) {
    //         scrollObserver.observePosition({
    //             el: this.containerRef.current,
    //             callback: this.handleViewportPostionChange,
    //             viewportPadding,
    //         });
    //         this.positionIsObserved = true;
    //     }
    // }

    // eslint-disable-next-line react/sort-comp, class-methods-use-this, consistent-return
    trackDishDetailsViewed = (dishId) => {
        const {
            categoryTitle,
            menuDishesQuery: { menuDishes },
            categoryId,
            subscriptionType,
        } = this.props;

        const dish = menuDishes.find(({ id }) => Number(id) === Number(dishId));

        if (!dish) return null;

        const {
            id,
            title,
            caption,
            price,
        } = dish;

        analyticService.push({
            id,
            title,
            caption,
            price,
            category: categoryTitle,
            categoryId,
            source: 'menu',
            eventName: 'Dish_Details_Viewed',
            subscriptionType,
        }).push({
            id,
            eventName: 'CityAds_Dish_Details_Viewed',
        });
    };

    renderScrollToTopPixel() {
        const { headerHeight } = this.props;

        const mobileStyles = {
            top: `-${headerHeight}px`,
        };

        return (
            <div
                ref={this.pixelForScrollRef}
                style={isDesktop() ? null : mobileStyles}
                styleName="pixel-for-scroll"
            />
        );
    }

    renderTitle() {
        const {
            categoryId,
            title: titleProp,
            locale,
        } = this.props;

        const title = titleProp ?? CATEGORY_TITLES_MOBILE[locale][categoryId];

        if (Array.isArray(title)) {
            return (
                <>
                    {title.map((part, i) => (
                        <React.Fragment key={part}>
                            {i !== 0 && <span className="micro">.&nbsp;</span>}
                            {part}
                        </React.Fragment>
                    ))}
                </>
            );
        }

        return title;
    }

    render() {
        const {
            isDishesUpdating,
            locale,
            categoryId,
            title,
            description,
            greenDishes,
            hitsDishes,
            nzDishes,
            kidsDishes,
            firstTimeDishes,
            delicaciesDishes,
            readyToEatDishes,
            spaDishObserver,
            hasSubcategoriesToRender,
            dishesWithSubcategories,
            menuDishesQuery,
            isReadyToEatVisible = true,
            ...otherProps
        } = this.props;

        const dishesWithSubcategoriesIds = dishesWithSubcategories.map((d) => d.id);
        const subcategoriesWithDishes = groupSubcategoriesByName(sortBy(
            dishesWithSubcategories,
            SUBCATEGORIES_ORDER
                .map((category) => (dish) => String(dish.subcategory_name) === category,
                )
                .reverse(),
        ));

        const menuDishes = hasSubcategoriesToRender
            ? menuDishesQuery.menuDishes
                .filter((d) => !dishesWithSubcategoriesIds.includes(d.id))
            : menuDishesQuery.menuDishes;

        const handleSendCollectionsAnalytics = (bannerName) => {
            analyticService.push({
                eventName: 'Collection_Click',
                banner_name: bannerName,
                source_name: 'menu',
            });
        };

        const isReadyToEatCategory = categoryId === CATEGORY_READY_TO_EAT && isReadyToEatVisible;
        const canRenderReadyToEatDishes = isReadyToEatVisible
            && Boolean(readyToEatDishes?.length)
            && categoryId === CATEGORY_MAIN;

        const titleContainerClasses = classNames({
            categoryBR1258TitleContainer: true,
            parent: hasSubcategoriesToRender || isReadyToEatCategory,
            'title-updating': isDishesUpdating,
            hasDescription: Boolean(description),
        });

        const titleClasses = classNames({
            categoryBR1258Title: true,
            parent: hasSubcategoriesToRender || isReadyToEatCategory,
        });

        return (
            <div
                ref={this.mainRef}
                styleName="categoryBR1258Root"
            >
                {categoryId !== CATEGORY_MAIN ? (
                    <>
                        <div styleName={titleContainerClasses}>
                            <Responsive
                                mobile={(
                                    <div
                                        styleName={titleClasses}
                                        className="subheadings-sh2-medium"
                                    >
                                        {categoryId === 'intresting' ? null : this.renderTitle()}
                                    </div>
                                )}
                                desktop={(
                                    <p styleName="category__title">
                                        {title === 'Новое' ? 'Самое интересное' : title}
                                    </p>
                                )}
                            />
                        </div>
                        {description ? (
                            <div
                                className="body-m-medium"
                                styleName="categoryBR1258Description"
                            >
                                {tp.execute(description)}
                            </div>
                        ) : null}
                    </>
                ) : null}
                <div
                    ref={this.containerRef}
                    styleName="dishes-container"
                    data-category_id={categoryId}
                >
                    {this.renderScrollToTopPixel()}
                    {isReadyToEatCategory ? (
                        <AdditionalDishesSelection
                            dishes={menuDishes}
                            options={{
                                title: null,
                                description: '',
                                rootLayout: 'slider',
                                cardsLayout: 'single',
                                isAlterImageUsed: true,
                                onOpenDetails: (id) => this.trackDishDetailsViewed(id),
                                isPriceHidden: true,
                            }}
                            spaDishObserver={spaDishObserver}
                            scrollElementName="readyToEat_slider"
                        />
                    ) : (
                        <DishesV2
                            categoryTitle={title}
                            delayTime={100}
                            isDishesUpdating={isDishesUpdating}
                            locale={locale}
                            categoryId={categoryId}
                            spaDishObserver={spaDishObserver}
                            menuDishesQuery={{ menuDishes }}
                            {...otherProps}
                        />
                    )}
                    <div styleName="additionalSelectionListContainer">
                        {greenDishes && greenDishes?.length && categoryId === CATEGORY_MAIN ? (
                            <div
                                styleName="additionalSelectionContainer"
                                onClick={() => handleSendCollectionsAnalytics('green')}
                                role="none"
                            >
                                <AdditionalDishesSelection
                                    dishes={greenDishes}
                                    options={{
                                        title: '#зож #лайт #green',
                                        description: '',
                                        rootLayout: 'grid',
                                        cardsLayout: 'green',
                                        isStubNeeded: greenDishes?.length > 5 && true,
                                        isLarge: true,
                                        onOpenDetails: (id) => this.trackDishDetailsViewed(id),
                                    }}
                                    spaDishObserver={spaDishObserver}
                                    scrollElementName="Green_collection"
                                />
                            </div>
                        ) : null}
                        {hitsDishes && hitsDishes?.length && categoryId === CATEGORY_MAIN ? (
                            <div
                                onClick={() => handleSendCollectionsAnalytics('hits')}
                                role="none"
                            >
                                <AdditionalDishesSelection
                                    dishes={hitsDishes}
                                    options={{
                                        title: 'Хиты',
                                        description: '',
                                        rootLayout: 'grid',
                                        cardsLayout: 'hits',
                                        isLarge: true,
                                        onOpenDetails: (id) => this.trackDishDetailsViewed(id),
                                    }}
                                    spaDishObserver={spaDishObserver}
                                    scrollElementName="Hits_collection"
                                />
                            </div>
                        ) : null}
                        {nzDishes && nzDishes?.length && categoryId === CATEGORY_MAIN ? (
                            <div
                                onClick={() => handleSendCollectionsAnalytics('zapas')}
                                role="none"
                            >
                                <AdditionalDishesSelection
                                    dishes={nzDishes}
                                    options={{
                                        title: 'Про запас',
                                        description: <>
                                            Хранится, пока не пригодится
                                            <br />
                                            1-2 месяца в морозилке
                                        </>,
                                        rootLayout: 'grid',
                                        cardsLayout: 'small',
                                        isAlterImageUsed: true,
                                        isLarge: true,
                                        onOpenDetails: (id) => this.trackDishDetailsViewed(id),
                                    }}
                                    spaDishObserver={spaDishObserver}
                                    scrollElementName="NZ_collection"
                                />
                            </div>
                        ) : null}
                        {kidsDishes && kidsDishes?.length && categoryId === CATEGORY_MAIN ? (
                            <div
                                onClick={() => handleSendCollectionsAnalytics('kids')}
                                role="none"
                            >
                                <AdditionalDishesSelection
                                    dishes={kidsDishes}
                                    options={{
                                        title: 'Понравится детям',
                                        description: 'от 4 до 99 лет',
                                        rootLayout: 'grid',
                                        cardsLayout: 'small',
                                        isLarge: true,
                                        isAlterImageUsed: true,
                                        onOpenDetails: (id) => this.trackDishDetailsViewed(id),
                                    }}
                                    spaDishObserver={spaDishObserver}
                                    scrollElementName="Kids_collection"
                                />
                            </div>
                        ) : null}
                        {firstTimeDishes && firstTimeDishes?.length && categoryId === CATEGORY_MAIN ? (
                            <div
                                onClick={() => handleSendCollectionsAnalytics('trial')}
                                role="none"
                            >
                                <AdditionalDishesSelection
                                    dishes={firstTimeDishes}
                                    options={{
                                        title: 'Как в первый раз',
                                        description: 'Блюда из пробного набора',
                                        rootLayout: 'grid',
                                        cardsLayout: 'small',
                                        isLarge: true,
                                        isAlterImageUsed: true,
                                        onOpenDetails: (id) => this.trackDishDetailsViewed(id),
                                    }}
                                    spaDishObserver={spaDishObserver}
                                    scrollElementName="Trial_collection"
                                />
                            </div>
                        ) : null}
                        {canRenderReadyToEatDishes ? (
                            <div
                                styleName="additionalSelectionContainer"
                                onClick={() => handleSendCollectionsAnalytics('ready_to_eat')}
                                role="none"
                            >
                                <AdditionalDishesSelection
                                    dishes={readyToEatDishes}
                                    options={{
                                        title: <>
                                            Готовые блюда
                                            <br />
                                            у вас дома
                                        </>,
                                        description: '',
                                        rootLayout: 'slider',
                                        cardsLayout: 'single',
                                        isAlterImageUsed: true,
                                        onOpenDetails: (id) => this.trackDishDetailsViewed(id),
                                        isPriceHidden: true,
                                    }}
                                    spaDishObserver={spaDishObserver}
                                    scrollElementName="readyToEat_slider"
                                />
                            </div>
                        ) : null}
                        {delicaciesDishes && delicaciesDishes?.length && categoryId === CATEGORY_SNACKS ? (
                            <div
                                styleName="additionalSelectionContainer"
                                onClick={() => handleSendCollectionsAnalytics('delicacies')}
                                role="none"
                            >
                                <AdditionalDishesSelection
                                    dishes={delicaciesDishes}
                                    options={{
                                        title: <>
                                            Готовые деликатесы
                                            <br />
                                            у вас дома
                                        </>,
                                        description: '',
                                        rootLayout: 'grid-shop',
                                        cardsLayout: 'shop',
                                        isAlterImageUsed: true,
                                        onOpenDetails: (id) => this.trackDishDetailsViewed(id),
                                        isPriceHidden: true,
                                    }}
                                    spaDishObserver={spaDishObserver}
                                    scrollElementName="Delicacy_slider"
                                />
                            </div>
                        ) : null}
                        {subcategoriesWithDishes.length ? (
                            <div>
                                {subcategoriesWithDishes.map((s) => (
                                    <AdditionalDishesSelection
                                        dishes={s.dishes}
                                        options={{
                                            title: s.subcategory_name,
                                            description: '',
                                            rootLayout: s.subcategory_name === 'Фермерские продукты'
                                                ? 'default'
                                                : 'slider',
                                            cardsLayout: s.subcategory_name === 'Фермерские продукты'
                                                ? 'default'
                                                : 'single',
                                            isAlterImageUsed: true,
                                            onOpenDetails: (id) => this.trackDishDetailsViewed(id),
                                        }}
                                        spaDishObserver={spaDishObserver}
                                        key={s.subcategory_name}
                                        scrollElementName={s.subcategory_name in SUBCATEGORIES_ANALYTICS_TITLES
                                            ? SUBCATEGORIES_ANALYTICS_TITLES[s.subcategory_name]
                                            : ''}
                                    />
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    }
}

MenuCategoryV2.propTypes = {
    locale: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,

    headerHeight: PropTypes.number.isRequired,

    categoryId: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,

    isDishesUpdating: PropTypes.bool.isRequired,
    isReadyToEatVisible: PropTypes.bool,
    isIntersectingOn: PropTypes.bool,

    viewportPadding: PropTypes.shape({
        top: PropTypes.number.isRequired,
        bottom: PropTypes.number,
    }).isRequired,
    onViewportPositionChange: PropTypes.func.isRequired,
};

const MenuCategoryAdapter = (props) => {
    const {
        isIntersectingOn = false,
        menuDishesQuery: { menuDishes },
        flatDishesList,
        categoryId,
    } = props;

    const { data: { description } } = useFragment({
        fragment: gql`fragment CAREGORY_DESCRIPTION on menuCategory {
                    description
                }`,
        from: {
            __ref: `menuCategory:${categoryId}`,
        },
    });
    const dishesWithSubcategories = useMemo(
        () => menuDishes
            ?.map((d) => ({
                id: d.id,
                subcategory_id: d.subcategory_id,
                subcategory_name: d.subcategory_name,
            }),
            ).filter((d) => Boolean(d.subcategory_name)),
        [menuDishes],
    );
    const hasSubcategoriesToRender = useMemo(
        () => dishesWithSubcategories.some((d) => Boolean(d.subcategory_name)),
        [dishesWithSubcategories],
    );

    const menuDispatchContext = useContext(MenuDispatchContext);
    const menuDataContext = useContext(MenuDataContext);
    const locationKey = useCategoryLocationKey();

    const isReadyToEatVisible = isCategoryVisibleForLocation(CATEGORY_READY_TO_EAT, locationKey);

    const {
        collections: {
            greenDishes,
            hitsDishes,
            nzDishes,
            kidsDishes,
            firstTimeDishes,
            delicaciesDishes,
            readyToEatDishes,
        },
    } = useMenuSelectionsDishes();

    const handleSendAnalytics = (intersectingDish) => {
        const isBasket = window.location.pathname.includes('/basket');
        const params = queryString.parse(window.location.search);
        const isMaxi = params?.st && params?.st.includes('maxi');
        const nonSpecificValue = isBasket ? 'basket' : 'menu';
        const source = isMaxi ? 'maxi' : nonSpecificValue;
        const intersectingDishIndex = flatDishesList
            ?.findIndex((e) => e.id === intersectingDish[0]?.id);
        analyticService.push({
            eventName: 'Product_Impression',
            dish: intersectingDish,
            dishIndex: intersectingDishIndex > -1
                ? intersectingDishIndex + 1
                : null,
            source_name: params?.src ?? source,
        });
    };

    const [spaDishObserver] = useProductImpressionObserver({
        dishesArray: menuDishes,
        analyticsHandler: handleSendAnalytics,
    });

    return (
        <MenuCategoryV2
            {...props}
            categoryId={categoryId}
            description={description}
            hasSubcategoriesToRender={hasSubcategoriesToRender}
            dishesWithSubcategories={dishesWithSubcategories}
            spaDishObserver={spaDishObserver}
            greenDishes={greenDishes}
            hitsDishes={hitsDishes}
            nzDishes={nzDishes}
            kidsDishes={kidsDishes}
            delicaciesDishes={delicaciesDishes}
            readyToEatDishes={readyToEatDishes}
            isReadyToEatVisible={isReadyToEatVisible}
            firstTimeDishes={firstTimeDishes}
            menuDispatchContext={menuDispatchContext}
            menuDataContext={menuDataContext}
            isIntersectingOn={isIntersectingOn}
        />
    );
};

export default MenuCategoryAdapter;

